import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Row, Col, Button, ButtonGroup } from 'react-bootstrap'
import moment from 'moment-timezone'
import * as s from './FootballAdmin.scss'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import Logins from '../../Main/Admin/Logins'
import FootballConfigSummary from './FootballConfigSummary'
import FootballSetActiveSeason from './FootballSetActiveSeason'
import FootballEditConfiguration from './FootballEditConfiguration'
import BroadcastMessage from './BroadcastMessage'
import FootballAdminSetSiteRegistration from './FootballAdminSetSiteRegistration'
import Modal from '@severed-links/common.modal'
import { resetEditConfig, saveConfig, getConfig } from '@severed-links/common.redherrings-reducers/footballAdmin'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import OnlineUsers from '../../../components/OnlineUsers'
import SendPushNotification from '../../Notifications/SendPushNotification'

const FootballAdminDashboard = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    const seasonId = useSelector(state => state.football.seasonId)
    const seasonName = useSelector(state => state.football.seasonName)
    const seasonMode = useSelector(state => state.football.seasonMode)
    const week = useSelector(state => state.football.week)
    const playoffWeek = useSelector(state => state.football.playoffWeek)
    const loginInfo = useSelector(state => state.footballAdmin.loginInfo)
    const editConfig = useSelector(state => state.footballAdmin.editConfig)
    const onlineUsers = useSelector(state => state.footballAdmin.onlineUsers) || []
    const dashboardSubTab = (params || {}).dashboardSubTab || null

    const saveConfiguration = () => {
        const stateToSave = { ...editConfig, teamRegistration: editConfig.teamRegistration ? 'Open' : 'Closed' }
        dispatch(saveConfig(stateToSave))
        .then(resp => {
            dispatch(createNotification({ 
                type: resp.payload.messageType, 
                headline: 'Save Configuration', 
                message: resp.payload.message, 
                timeout: 6000 
            }))
            dispatch(getConfig(resp.payload.data))
        })
        dispatch(resetEditConfig())
        navigate('/football/admin')
    }

    var buttonContainers = [
        { icon: 'envelope', text: 'Send email', description: 'Broadcast a message to players', onClick: () => navigate(`/football/admin/utility/broadcast-message`) },
        { icon: 'leaf', text: 'Active season', description: 'Change the current football season', onClick: () => navigate(`/football/admin/utility/set-active-season`) },
        { icon: 'cog', text: 'Configuration', description: 'Edit registration and payment settings', onClick: () => navigate(`/football/admin/utility/edit-season-config`) },
        ...(seasonMode === 'Preseason' ? [
            { icon: 'list-alt', text: 'Team data', description: 'Review teams and player requests', onClick: () => navigate(`/football/admin/preseason/team-data/${seasonName}`) },
            { icon: 'list', text: 'NFL scheduler', description: 'Manage the NFL game schedule', onClick: () => navigate(`/football/admin/preseason/nfl-scheduler/${seasonName}`) },
            { icon: 'th-list', text: 'League scheduler', description: 'Build the pool matchup schedule', onClick: () => navigate(`/football/admin/preseason/league-scheduler/${seasonName}`) },
            { icon: 'money-bill-alt', text: 'Payment log', description: 'Review registration payments', onClick: () => navigate(`/football/admin/preseason/payment-log/${seasonName}`) },
        ] : []),
        ...((seasonMode === 'Regular' || seasonMode === 'Preseason') ? [
            { icon: 'rotate', text: 'Update manager', description: 'Run the weekly operations checklist', onClick: () => navigate(`/football/admin/regular-season/update-manager/${seasonName}/week/${week}`) },
        ] : []),
    ]
    return (
        <main className={s.container}>
            <header className={s.dashboardHeader}>
                <div className={s.eyebrow}>Football administration</div>
                <h1>Administrator Dashboard</h1>
                <p>Manage the {seasonName} season, weekly operations, and player activity.</p>
            </header>
            <Row className={s.dashboardGrid}>
                <Col lg={7} xs={12}>
                    <section className={s.dashboardPanel}>
                        <FootballConfigSummary />
                    </section>
                    <section className={s.toolsPanel}>
                        <div className={s.sectionHeading}>
                            <div>
                                <div className={s.eyebrow}>Operations</div>
                                <h2>Admin tools</h2>
                            </div>
                            <FootballAdminSetSiteRegistration />
                        </div>
                    <div className={s.dashboardButtonContainer}>
                        {buttonContainers.map(b =>
                            <Button variant='light' className={s.toolButton} key={`football-admin-dashboard-${b.text}`} onClick={() => b.onClick()}>
                                <span className={s.toolIcon}><FontAwesomeIcon name={b.icon} /></span>
                                <span className={s.toolCopy}>
                                    <strong>{b.text}</strong>
                                    <small>{b.description}</small>
                                </span>
                                <FontAwesomeIcon name='chevron-right' />
                            </Button>
                        )}
                    </div>
                    </section>
                </Col>
                <Col lg={5} xs={12}>
                    <aside className={s.activityPanel}>
                        <div className={s.sectionHeading}>
                            <div>
                                <div className={s.eyebrow}>Player activity</div>
                                <h2>Online now <span className={s.onlineCount}>{onlineUsers.length}</span></h2>
                            </div>
                            <SendPushNotification />
                        </div>
                        <OnlineUsers users={onlineUsers} noBottomMargin />
                        <div className={s.loginSection}><Logins isDashboard /></div>
                    </aside>
                </Col>
            </Row>

            <Modal heading='Broadcast Message'
                show={dashboardSubTab === 'broadcast-message'}
                onClose={() => navigate(`/football/admin`)}
                actionButtonText={null}>
                <BroadcastMessage />
            </Modal>

            <Modal heading='Set active season' size={'sm'}
                show={dashboardSubTab === 'set-active-season'}
                onClose={() => navigate(`/football/admin`)}
                actionButtonText={null}>
                <FootballSetActiveSeason />
            </Modal>

            <Modal heading={'Edit ' + seasonName + ' Season configuration'} size={null}
                show={dashboardSubTab === 'edit-season-config'}
                onClose={() => { navigate(`/football/admin`); resetEditConfig(); }}
                actionButtonOnClick={() => saveConfiguration()}
                actionButtonText={'save configuration'}>
                <div className={s.editConfigurationModalContainer}><FootballEditConfiguration /></div>                    
            </Modal>
        </main>
    )
}

export default FootballAdminDashboard
